import { useMemo } from "react";
import type { PasswordRule, StrengthConfig } from "./types";
import { DEFAULT_PASSWORD_RULES, DEFAULT_STRENGTH_CONFIG } from "./data";
import {
    mergeRules,
    evaluatePasswordRules,
    calculateStrengthLevel,
} from "./helper";
import StrengthBar from "./StrengthBar";
import StrengthLabel from "./StrengthLabel";
import RequirementsList from "./RequirementsList";

interface PasswordStrengthCheckerProps {
    password?: string;
    customRules?: PasswordRule[];
    strengthConfig?: StrengthConfig;
    strengthSegments?: number;
    showPasswordText?: boolean;
    showRequirements?: boolean;
    className?: string;
}

const PasswordStrengthChecker = ({
    password = "",
    customRules = [],
    strengthConfig = DEFAULT_STRENGTH_CONFIG,
    strengthSegments = 4,
    showPasswordText = false,
    showRequirements = true,
    className = "",
}: PasswordStrengthCheckerProps) => {
    const allRules = useMemo(
        () => mergeRules(DEFAULT_PASSWORD_RULES, customRules),
        [customRules]
    );

    const ruleResults = useMemo(
        () => evaluatePasswordRules(password, allRules),
        [password, allRules]
    );

    const strengthLevel = useMemo(
        () => calculateStrengthLevel(password, ruleResults),
        [password, ruleResults]
    );

    const visibleRules = useMemo(
        () => ruleResults.filter((rule) => !rule.hidden),
        [ruleResults]
    );

    return (
        <div className={`font-SatoshiR ${className}`}>
            {showPasswordText && <span className='block mb-2'>{password}</span>}

            <StrengthBar
                strengthLevel={strengthLevel}
                strengthSegments={strengthSegments}
                password={password}
                strengthConfig={strengthConfig}
            />

            <StrengthLabel
                password={password}
                strengthLevel={strengthLevel}
                strengthConfig={strengthConfig}
            />

            {showRequirements && visibleRules.length > 0 && (
                <RequirementsList visibleRules={visibleRules} />
            )}
        </div>
    );
};

export default PasswordStrengthChecker;
